import { Component, type ErrorInfo, type ReactNode } from 'react'
import { WarningIcon } from '@/components/icons'

/**
 * 顶层错误边界。
 *
 * AGENTS.md 2 禁止 class 组件 —— 这是**唯一的例外**，而且是被迫的：
 * React 至今没有提供函数式的错误捕获 API（`componentDidCatch` /
 * `getDerivedStateFromError` 只存在于 class 上）。没有它，任何渲染期异常都会
 * 让整个页面变成空白，用户连「刷新」都不知道该点哪里。
 *
 * 引一个 `react-error-boundary` 依赖也是选项，但那个包本身也只是这段代码的
 * 封装，为 30 行换一个依赖不值得。
 */

interface State {
  error: Error | null
}

export class RootErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 留在控制台，方便开发期定位；生产环境这里是接入上报服务的位置。
    console.error('[boundary] 渲染期异常', error, info.componentStack)
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md space-y-3 text-center">
          <WarningIcon className="mx-auto size-10 text-danger" />
          <h1 className="text-lg font-semibold text-foreground">页面出错了</h1>
          <p className="text-sm leading-6 text-muted-foreground">
            界面遇到了一个未预期的错误。刷新页面通常可以恢复；如果反复出现，请联系管理员。
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex h-10 items-center rounded-card bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
          >
            刷新页面
          </button>
        </div>
      </div>
    )
  }
}
